import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import formatGroupedValues from "../utils/formatGroupedValues";
import capitalizeFilterLabel from "../utils/capitalizeFilterLabel";
import { EMBEDDED_FILTER_LABEL_CLASS } from "../../embedded-filter/constants";

const COMPACT_LABEL_CLASS =
  "mb-0.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-800";

function useDebouncedValue(value, delayMs = 150) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function normalizeOptions(options, formatLabel) {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  const toLabel = (value) => {
    if (typeof formatLabel === "function") return formatLabel(value);
    return capitalizeFilterLabel(value);
  };

  return options
    .map((opt) => {
      if (opt == null) return null;
      if (typeof opt === "object" && opt.value != null) {
        const value = String(opt.value);
        if (!value || seen.has(value)) return null;
        seen.add(value);
        const label =
          opt.label != null && String(opt.label).trim() !== value
            ? String(opt.label)
            : toLabel(value);
        return { value, label };
      }
      const value = String(opt).trim();
      if (!value || seen.has(value)) return null;
      seen.add(value);
      return { value, label: toLabel(value) };
    })
    .filter(Boolean);
}

/** 0 = show all options (no UI cap) */
const DEFAULT_MAX_VISIBLE = 0;

export default function FilterMultiSelect({
  id,
  label,
  options = [],
  value = [],
  onChange,
  placeholder = "Select…",
  formatLabel,
  /** compact = docked Atlas panel; embedded = floating RichAudits-style panel */
  labelVariant = "compact",
  loading = false,
  onQueryChange,
  maxVisibleOptions = DEFAULT_MAX_VISIBLE,
  minQueryLength = 0,
}) {
  const labelClassName =
    labelVariant === "embedded" ? EMBEDDED_FILTER_LABEL_CLASS : COMPACT_LABEL_CLASS;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const debouncedQuery = useDebouncedValue(query);

  const normalizedOptions = useMemo(
    () => normalizeOptions(options, formatLabel),
    [options, formatLabel],
  );
  const selected = Array.isArray(value) ? value : [];

  const display = (val) => {
    const match = normalizedOptions.find((o) => o.value === val);
    if (match?.label) return match.label;
    if (typeof formatLabel === "function") return formatLabel(val);
    return capitalizeFilterLabel(val);
  };

  const selectedSummary = useMemo(
    () => formatGroupedValues(selected, display, 3),
    [selected, normalizedOptions, formatLabel],
  );

  const filteredOptions = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    const selectedSet = new Set(selected);

    if (minQueryLength > 0 && q.length < minQueryLength) {
      return normalizedOptions.filter((opt) => selectedSet.has(opt.value));
    }

    const tokens = q ? q.split(/\s+/).filter(Boolean) : [];
    let matched = !q
      ? normalizedOptions
      : normalizedOptions.filter((opt) => {
          const val = opt.value.toLowerCase();
          const lbl = opt.label.toLowerCase();
          if (val.includes(q) || lbl.includes(q)) return true;
          return tokens.length > 0 && tokens.every((t) => val.includes(t) || lbl.includes(t));
        });

    if (maxVisibleOptions > 0 && matched.length > maxVisibleOptions) {
      const selectedFirst = matched.filter((opt) => selectedSet.has(opt.value));
      const rest = matched.filter((opt) => !selectedSet.has(opt.value));
      const room = Math.max(maxVisibleOptions - selectedFirst.length, 0);
      matched = [...selectedFirst, ...rest.slice(0, room)];
    }

    return matched;
  }, [normalizedOptions, debouncedQuery, maxVisibleOptions, minQueryLength, selected]);

  const showTypeToSearchHint =
    minQueryLength > 0 && debouncedQuery.trim().length < minQueryLength;
  const showResultCapHint =
    maxVisibleOptions > 0 &&
    !showTypeToSearchHint &&
    !debouncedQuery.trim() &&
    normalizedOptions.length > maxVisibleOptions;

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const t = setTimeout(() => searchRef.current?.focus(), 50);
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const toggle = (val) => {
    const exists = selected.includes(val);
    onChange(exists ? selected.filter((v) => v !== val) : [...selected, val]);
  };

  const toggleVisible = () => {
    const visibleValues = filteredOptions.map((o) => o.value);
    if (visibleValues.length === 0) return;
    const allSelected = visibleValues.every((v) => selected.includes(v));
    if (allSelected) {
      onChange(selected.filter((v) => !visibleValues.includes(v)));
    } else {
      onChange([...new Set([...selected, ...visibleValues])]);
    }
  };

  const control = (
    <div ref={containerRef} className="relative">
      <button
        id={`${id}-control`}
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
        className={[
          "flex h-8 w-full items-center gap-1 rounded border bg-white px-2 text-left text-xs transition-all",
          open
            ? "border-gray-500 ring-1 ring-gray-400"
            : "border-gray-300 hover:border-gray-400",
        ].join(" ")}
      >
        <span
          className={[
            "min-w-0 flex-1 truncate",
            selected.length === 0 ? "text-gray-500" : "text-gray-900",
          ].join(" ")}
          title={selected.length > 0 ? selected.map(display).join(", ") : undefined}
        >
          {selected.length === 0 ? placeholder : selectedSummary}
        </span>
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-[80] mt-1 flex max-h-56 flex-col overflow-hidden rounded border border-gray-200 bg-white shadow-lg">
          <div className="space-y-1 border-b border-gray-100 bg-gray-50 p-1.5">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                onQueryChange?.(e.target.value);
              }}
              placeholder={`Search…`}
              className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
            <div className="flex items-center justify-between gap-2 px-0.5">
              <button
                type="button"
                onClick={toggleVisible}
                className="text-[10px] font-medium text-gray-600 hover:text-gray-900"
              >
                {filteredOptions.length > 0 &&
                filteredOptions.every((o) => selected.includes(o.value))
                  ? "Deselect visible"
                  : "Select visible"}
              </button>
              {selected.length > 0 ? (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[10px] font-medium text-gray-500 hover:text-gray-800"
                >
                  Clear
                </button>
              ) : null}
            </div>
          </div>
          <ul className="min-h-[2rem] overflow-y-auto py-0.5" role="listbox" aria-multiselectable="true">
            {loading ? (
              <li className="px-2 py-4 text-center text-[10px] text-gray-500">Loading…</li>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const checked = selected.includes(opt.value);
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      onClick={() => toggle(opt.value)}
                      className={[
                        "flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors hover:bg-gray-50",
                        checked ? "bg-gray-100 font-medium text-gray-900" : "text-gray-700",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border transition-colors",
                          checked ? "border-gray-600 bg-gray-600" : "border-gray-300 bg-white",
                        ].join(" ")}
                      >
                        {checked ? <span className="h-1 w-1 rounded-full bg-white" /> : null}
                      </span>
                      <span className="truncate">{opt.label}</span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="px-2 py-4 text-center text-[10px] italic text-gray-400">
                {showTypeToSearchHint
                  ? `Type at least ${minQueryLength} character${minQueryLength === 1 ? "" : "s"} to search`
                  : normalizedOptions.length > 0
                    ? "No matches"
                    : "No options"}
              </li>
            )}
            {showResultCapHint ? (
              <li className="border-t border-gray-100 px-2 py-1.5 text-center text-[10px] text-gray-500">
                Showing first {maxVisibleOptions}. Type to narrow results.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );

  if (!label) return control;

  return (
    <div className="space-y-0.5">
      <label
        htmlFor={`${id}-control`}
        className={labelClassName}
      >
        {label}
      </label>
      {control}
    </div>
  );
}
